/* src/components/block/broadcastingChannelManagment/BroadcastingChannelList.tsx */


import   React                               ,
       { useState                            ,
	     useEffect                           ,
	     useCallback                         ,
	     useMemo                             ,
         forwardRef                          ,
         useImperativeHandle                 }  from 'react';
import   classNames                             from 'classnames';

import   styles                                 from './BroadcastingChannelList.module.scss';

import   Field                                  from '../../widget/inputs/Field';
import   Select                                 from '../../widget/inputs/Select';
import   AnimatedWaitingIcon2                    from '../../widget/animations/animatedWaitingIcon2';
import   Window                                 from '../../widget/windows/window';
import   ColumnTitle                            from '../../widget/dataDisplay/labels/ColumnTitle'

import   BroadcastingChannelRow                 from './BroadcastingChannelRow';
import   BroadcastingChannelSheet               from './BroadcastingChannelSheet';
import   BroadcastingChannelCategoryManager     from '../broadcastingChannelCategoryManagment/BroadcastingChannelCategoryManager';

import { BroadcastingChannelCategory         ,
         BroadcastingChannel                 }  from '../../../dataTypes/broadcastingManagment/types';
import { Product                             }  from '../../../dataTypes/productManagment/types';
import { getBroadcastingChannelList          ,
         deleteBroadcastingChannel           }  from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { getProducts                         }  from '../../../backendServices/productManagment/api_product';

import { afficherLimite                    ,
         quotaAtteint                        }  from '../../../dataTypes/adminManagment/quota';
import { BUY_CHANNEL_LABEL                  ,
         openBuyChannel                      }  from '../../../utils/utils_channelPurchase';

import { useTypedSelector, useTypedDispatch  }  from '../../../store/useTypeSelector';
import { RootState                           }  from '../../../store/generalReducer';
import { refreshAdmin                        }  from '../../../store/adminAuthenticationManagment/actions';
import { setPanelLeaveGuard                  }  from '../../widget/navigation/tab';

export interface BroadcastingChannelListHandle {
  /** Revient à la liste sans démonter la fiche déjà ouverte (live conservé en mémoire). */
  goToList: () => void;
  /** Ouvre la fiche d'une chaîne, sur l'onglet donné (index de BroadcastingChannelSheet) —
   *  boutons « Fiche » et « Régie » du Dashboard. La fiche déjà ouverte de la même chaîne
   *  reste telle quelle (son direct n'est pas coupé). */
  openChannel: (id: number, tabIndex?: number) => void;
}

export const BroadcastingChannelList = forwardRef<BroadcastingChannelListHandle>((_props, ref) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const dispatch           = useTypedDispatch();

  const [ categorySet                   ] = useState<BroadcastingChannelCategory[]>([]);
  // Source unique de vérité : la liste complète telle que renvoyée par le backend.
  // La liste affichée en est dérivée (useMemo plus bas) au lieu d'être un second état
  // à maintenir à la main — toute mise à jour qui n'aurait touché qu'une des deux
  // copies les désynchronisait silencieusement.
  const [ broadcastingChannelSet        , setBroadcastingChannelSet        ] = useState<BroadcastingChannel[]>([]);
  const [ productArray                  , setProductArray                  ] = useState<Product[]>([]);

  const [ showWaitingIcon               , setShowWaitingIcon               ] = useState(false);

  const [ selectedCategory              , setSelectedCategory              ] = useState('Toutes');
  const [ selectedCategoryIndex         , setSelectedCategoryIndex         ] = useState(0);
  const [ filter                        , setFilter                        ] = useState('');

  // La fiche ouverte est repérée par id, jamais par position dans la liste : un index
  // désigne une autre chaîne dès que la liste filtrée change (recherche, suppression,
  // rechargement), et sortait du tableau au point de faire planter le rendu.
  const [ editBroadcastingChannelId     , setEditBroadcastingChannelId     ] = useState<number | null>(null);
  // Onglet d'arrivée quand la fiche est ouverte de l'extérieur (Dashboard, 2026-10-17) ;
  // undefined = l'onglet habituel (Tableau de bord).
  const [ editInitialTabIndex           , setEditInitialTabIndex           ] = useState<number | undefined>(undefined);
  const [ createNewBroadcastingChannel  , setCreateNewBroadcastingChannel  ] = useState<boolean>(false);
  // Id de la chaîne née de la fiche de création (« Créer la chaîne » cliqué), null tant
  // qu'elle n'existe pas : c'est ce qui distingue une création abandonnable d'une chaîne
  // déjà enregistrée qu'on quitte (2026-10-20).
  const [ createdChannelId              , setCreatedChannelId              ] = useState<number | null>(null);
  // Geste en attente de la confirmation « Quitter la création ? » (null = pas de question).
  const [ leaveCreationAction           , setLeaveCreationAction           ] = useState<(() => void) | null>(null);
  // Numéro de la fiche de création : une nouvelle création remonte une fiche neuve, même si
  // la précédente (déjà créée, son activation en cours) est encore montée derrière la liste.
  const [ creationSeq                   , setCreationSeq                   ] = useState(0);


  const [ showMessageWindow      , setShowMessageWindow      ] = useState              ( false );
  const [ message                , setMessage                ] = useState              ( <></> );
  // Distingue le refus de quota (bouton « Acheter une chaîne ») des autres messages
  // (échec de suppression…) qui réutilisent la même fenêtre.
  const [ isQuotaMessage         , setIsQuotaMessage         ] = useState              ( false );
  // Compte rendu de l'action « Acheter une chaîne », affiché au-dessus de la fenêtre
  // de quota qui reste ouverte derrière — cf. BroadcastingChannelSheet.
  const [ purchaseNotice         , setPurchaseNotice         ] = useState              ( ''    );

  // Supprimer une chaîne est irréversible (et désactive le slot au passage) : on
  // demande confirmation avant d'appeler le backend. La chaîne visée est retenue ici
  // le temps de la question.
  const [ showDeleteConfirmation , setShowDeleteConfirmation ] = useState              ( false );
  const [ broadcastingChannelToDelete, setBroadcastingChannelToDelete ] = useState<BroadcastingChannel | null>( null );

  // Contrôle uniquement l'affichage liste/fiche : la fiche déjà ouverte reste montée
  // (display:none) pour ne pas perdre le direct (écran partagé, connexions WebRTC…)
  // quand on revient sur la liste puis qu'on rouvre la même chaîne.
  const [ showList               , setShowList               ] = useState              ( true  );

  const [ showCategoryManager    , setShowCategoryManager    ] = useState              ( false );
  // Faux tant que la première lecture n'a pas répondu : un créateur à chaîne unique ne doit
  // pas voir la liste passer avant que sa fiche ne s'ouvre.
  const [ initialLoadDone        , setInitialLoadDone        ] = useState              ( false );

  // Créateur avec une seule chaîne (2026-10-20) : la liste ne sert à rien, on arrive
  // directement sur le tableau de bord de la chaîne, qui porte alors « Créer une chaîne ».
  // Le Superadmin garde sa liste.
  const singleChannel = authentication.admin?.role !== 'ADMIN' && broadcastingChannelSet.length === 1
                          ? broadcastingChannelSet[0]
                          : null;

  // Le panneau « Chaînes » est déclaré keepMounted dans desktop.tsx : ce composant n'est
  // jamais démonté pendant la session. Un chargement au seul montage laissait donc la
  // liste figée sur l'état du login — invisible pour tout ce qui est modifié dans la
  // fiche (nom, mode, actif via le dashboard, payant, public…). On recharge donc à
  // chaque retour sur la liste, en silencieux pour ne pas faire clignoter le panneau.
  const loadBroadcastingChannels = useCallback(async (silent: boolean = false) => {

    try
     {
      if (!silent) { setShowWaitingIcon(true); }

      // Les produits sont relus avec les chaînes : « payant » se déduit du produit
      // d'abonnement associé (broadcastingChannelIsPaid). Passer payant↔gratuit crée ou
      // désactive ce produit ; sans relecture la liste gardait l'ancien état.
      const [broadcastingChannelResult, productResult] = await Promise.all([
        getBroadcastingChannelList(true, authentication.admin?.id),
        getProducts(undefined, authentication.admin?.id)
      ]);

      if (broadcastingChannelResult.payload)
       {
        setBroadcastingChannelSet (broadcastingChannelResult.payload);
       }

      if (productResult.payload)
       {
        setProductArray (productResult.payload);
       }
     }
    catch (error)
     {
      console.error('Error fetching broadcasting channels:', error);
     }
    finally
     {
      if (!silent) { setShowWaitingIcon(false); }
     }
  }, [authentication.admin?.id]);

  // Liste affichée : elle se met à jour seule toutes les 20 s (chaîne activée ou arrêtée depuis l'appli
  // ou un autre poste, présence sur le portail public, catégorie, nom...). Seule la liste des chaînes
  // est relue, pas les produits ; rien n'est relu onglet caché, ni pendant qu'une fiche est ouverte
  // (elle a son propre relevé).
  useEffect(() => {
    if (!showList || !initialLoadDone) return;
    let inFlight = false;
    const timer = window.setInterval(async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      try {
        const result = await getBroadcastingChannelList(true, authentication.admin?.id);
        if (result.payload) { setBroadcastingChannelSet(result.payload); }
      } catch { /* le relevé suivant rattrape */ }
      finally { inFlight = false; }
    }, 20000);
    return () => window.clearInterval(timer);
  }, [showList, initialLoadDone, authentication.admin?.id]);

  // Ferme la fiche de création. Le drapeau createNewBroadcastingChannel n'était levé que par
  // Annuler : quitter par le menu le laissait en place et toute chaîne ouverte ensuite depuis
  // la liste retombait sur la fiche de création. Une chaîne déjà créée reste ouverte, comme
  // une fiche ordinaire.
  const leaveCreation = useCallback(() => {
    if (createdChannelId !== null)
     {
      setEditInitialTabIndex(undefined);
      setEditBroadcastingChannelId(createdChannelId);
     }
    setCreateNewBroadcastingChannel(false);
    setCreatedChannelId(null);
  }, [createdChannelId]);

  // Fiche de création affichée et « Créer la chaîne » jamais cliqué : la quitter perd la saisie.
  const creationPending = createNewBroadcastingChannel && createdChannelId === null && !showList;

  // Quitter la création : on demande d'abord si rien n'a été enregistré, sinon on passe.
  const confirmLeaveCreation = useCallback((proceed: () => void) => {
    if (creationPending) { setLeaveCreationAction(() => proceed); return; }
    proceed();
  }, [creationPending]);

  // Quitter la section Chaînes par le menu pendant une création non enregistrée : même
  // question ; Oui abandonne la création puis change de section.
  useEffect(() => {
    setPanelLeaveGuard(creationPending
      ? (proceed) => {
          setLeaveCreationAction(() => () => { leaveCreation(); setShowList(true); proceed(); });
          return true;
        }
      : null);
    return () => setPanelLeaveGuard(null);
  }, [creationPending, leaveCreation]);

  useImperativeHandle(ref, () => ({
    goToList: () => {
      confirmLeaveCreation(() => {
        leaveCreation();
        setShowList(true);
        setShowCategoryManager(false);
        loadBroadcastingChannels(true);
      });
    },
    openChannel: (id: number, tabIndex?: number) => {
      confirmLeaveCreation(() => {
        setShowCategoryManager(false);
        setCreateNewBroadcastingChannel(false);
        setCreatedChannelId(null);
        setEditInitialTabIndex(tabIndex);
        setEditBroadcastingChannelId(id);
        setShowList(false);
      });
    },
  }), [loadBroadcastingChannels, confirmLeaveCreation, leaveCreation]);

  useEffect(() => {

    const fetchData = async () => {

      try
       {
        setShowWaitingIcon(true);

        const [broadcastingChannelResult,productResult] = await Promise.all([
          getBroadcastingChannelList(true, authentication.admin?.id),
          getProducts(undefined, authentication.admin?.id)
        ]);

        if (broadcastingChannelResult.payload)
         {
          setBroadcastingChannelSet (broadcastingChannelResult.payload);
         }

        if (productResult.payload)
         {
          setProductArray (productResult.payload);
         }

//         if (categoriesResult.payload)
//          {
//           setCategorySet(categoriesResult.payload.sort((a, b) => a.rank - b.rank));
//          }
       }
      catch (error)
       {
        console.error('Error fetching data:', error);
       }
      finally
       {
        setShowWaitingIcon(false);
        setInitialLoadDone(true);
       }
    };

    fetchData();
  }, []);

  // Chaîne unique : chaque fois que la liste devrait s'afficher (arrivée, clic sur le menu
  // Chaînes, abandon d'une création, rechargement), on ouvre la fiche à sa place. La fiche
  // déjà ouverte de cette chaîne reste montée (même clé) : son direct n'est pas coupé.
  // Une création quittée par le menu est abandonnée : sans liste, rien ne la rouvrirait.
  useEffect(() => {
    if (!singleChannel || !showList || showCategoryManager) { return; }
    if (editBroadcastingChannelId !== singleChannel.id) { setEditInitialTabIndex(undefined); }
    setCreateNewBroadcastingChannel(false);
    setEditBroadcastingChannelId(singleChannel.id);
    setShowList(false);
  }, [singleChannel, showList, showCategoryManager, editBroadcastingChannelId]);

  const checkFilter = (subChaine: string, chaine: string) => {
    if (subChaine === '') return true;
    return chaine.toUpperCase().includes(subChaine.toUpperCase());
  };

  // Liste affichée = dérivée de la liste complète. Recalculée automatiquement à chaque
  // rechargement ou changement de filtre, sans avoir à la reconstruire dans les handlers.
  const visibleBroadcastingChannelSet = useMemo(() => {
    return broadcastingChannelSet.filter((broadcastingChannel) => {
      const matchesCategory =
        selectedCategoryIndex === 0 ||
        broadcastingChannel.categoriesIds.includes(categorySet[selectedCategoryIndex - 1]?.id);
      const matchesFilter = checkFilter(filter, broadcastingChannel.name);
      return matchesCategory && matchesFilter;
    });
  }, [broadcastingChannelSet, selectedCategoryIndex, filter, categorySet]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setSelectedCategoryIndex(e.target.selectedIndex);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const handleEditionClick = (index: number) => {
    const broadcastingChannelToEdit = visibleBroadcastingChannelSet[index];
    if (!broadcastingChannelToEdit) { return; }

    // Chaîne éphémère : créée et pilotée par un autre système (colonne BroadcastingChannel.ephemeral,
    // posée à la main en base). La fiche s'ouvre normalement — sur Tableau de bord, qui ne touche
    // ni webcam ni devices — mais les autres onglets (Configuration, Programmation, Régie, Restream)
    // restent bloqués dans BroadcastingChannelSheet : c'est là, pas ici, qu'est la garde.
    setCreateNewBroadcastingChannel(false);
    setCreatedChannelId(null);
    setEditInitialTabIndex(undefined);
    setEditBroadcastingChannelId(broadcastingChannelToEdit.id);
    setShowList(false);
  };

  const handleDeleteClick = (broadcastingChannelId: number) => {

    const channel = broadcastingChannelSet.find((broadcastingChannel) => broadcastingChannel.id === broadcastingChannelId);

    if (!channel) {
      console.warn(`Media avec ID ${broadcastingChannelId} introuvable.`);
      return;
    }

    // On ne supprime pas une chaîne tant qu'elle n'est pas totalement éteinte : la
    // suppression démonte l'infra (container, MediaMTX, HAProxy) et libère le slot, ce
    // qui ne doit pas se produire pendant qu'elle diffuse ou qu'elle est en transition.
    if (channel.channelStatus !== 'inactive') {
      setMessage(<>Cette chaîne est encore active. Vous devez d'abord l'éteindre avant de pouvoir la supprimer.</>);
      setIsQuotaMessage(false);
      setShowMessageWindow(true);
      return;
    }

    setBroadcastingChannelToDelete(channel);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmation = async (confirmed: boolean) => {

    setShowDeleteConfirmation(false);

    if (!confirmed || !broadcastingChannelToDelete) {
      setBroadcastingChannelToDelete(null);
      return;
    }

    const broadcastingChannelId = broadcastingChannelToDelete.id;

    setShowWaitingIcon(true);
    try
     {
      const response = await deleteBroadcastingChannel (broadcastingChannelToDelete.id);

      if (response.code === 1)
            { // ✅ Suppression réussie
             setBroadcastingChannelSet((prev) => prev.filter((broadcastingChannel) => broadcastingChannel.id !== broadcastingChannelId));
             // La fiche encore montée pointerait sur une chaîne qui n'existe plus.
             setEditBroadcastingChannelId((prev) => prev === broadcastingChannelId ? null : prev);
             console.log(`Broadcasting channel avec ID ${broadcastingChannelId} supprimé.`);
             setMessage(<>Le canal de diffusion a été supprimé</>);
             // Supprimer une chaîne active la désactive au passage côté backend : le
             // slot de chaîne et les devices de sa régie sont libérés là-bas. On
             // recharge donc les compteurs au lieu de les recalculer à l'aveugle ici
             // (une chaîne déjà inactive n'en consommait aucun).
             if (authentication.admin?.email) {
               dispatch(refreshAdmin(authentication.admin.email));
             }
            }
       else {
             console.warn(`Impossible de supprimer le Broadcasting channel ID ${broadcastingChannelId} : ${response.comment}`);
             setMessage (
                         response.code === -103
                         ? <>Impossible de supprimer le canal de diffusion car il est déjà lié à d'autres ressources</>
                         : <>Impossible de supprimer le canal de diffusion</>
                        );
            }
     }
    catch (error)
     {
      console.error(`Erreur lors de la suppression du Broadcasting channel ID ${broadcastingChannelId}:`, error);
      setMessage(<>Impossible de supprimer le canal de diffusion</>);
     }

    setShowWaitingIcon(false);
    setBroadcastingChannelToDelete(null);
    setIsQuotaMessage(false);
    setShowMessageWindow(true);
  };

  // La colonne « Actif » de la liste est une PUCE, pas un interrupteur : on n'active plus
  // une chaîne depuis la liste, seulement depuis sa fiche (BroadcastingChannelSheet,
  // handleActiveToggle). Le handler qui vivait ici — optimiste puis rechargement — a donc
  // été retiré le 2026-08-17 : il était devenu injoignable le 2026-08-14, quand la colonne
  // est passée en puce, et il dupliquait en moins complet celui de la fiche (lequel gère en
  // plus le 409 de quota, le préavis d'arrêt d'enregistrement et la revérification avant
  // rollback). Le rafraîchissement des compteurs d'admin après bascule vit là-bas aussi.

  // Le quota de chaînes est contrôlé ICI, avant d'ouvrir la fiche : le refus arrivait
  // auparavant à l'enregistrement (409 du serveur), c'est-à-dire après avoir fait remplir
  // toute la fiche pour rien.
  //
  // Le décompte est celui du serveur, à l'identique : les chaînes EXISTANTES (supprimées
  // exclues) du propriétaire connecté — BroadcastingChannelList_detailedVersion applique
  // exactement les critères du contrôle de BroadcastingChannelCreation
  // (['deleted' => false, 'owner' => adminId]). On relit la liste au lieu de se fier à
  // celle affichée : elle peut dater d'un autre poste.
  //
  // Ce contrôle ne REMPLACE pas celui du serveur, qui reste le seul à faire autorité et
  // continue de refuser en 409 (cf. BroadcastingChannelSheet) : si la lecture échoue, on
  // laisse passer plutôt que de bloquer une création légitime.
  const handleNewBroadcastingChannelCreationClick = async () => {

    const adminId  = authentication.admin?.id;
    const maxCount = authentication.admin?.nbChannelsMax ?? 0;

    // Sans propriétaire identifié, le serveur ne contrôle rien non plus : rien à pré-vérifier.
    if (adminId)
     {
      try
       {
        setShowWaitingIcon(true);

        const broadcastingChannelResult = await getBroadcastingChannelList(true, adminId);

        if (broadcastingChannelResult.code === 1 && broadcastingChannelResult.payload)
         {
          const usedCount = broadcastingChannelResult.payload.length;

          setBroadcastingChannelSet (broadcastingChannelResult.payload);

          if (quotaAtteint(usedCount, maxCount))
           {
            setMessage (<>Quota de chaînes atteint ({usedCount}/{afficherLimite(maxCount)}) : supprimez une chaîne existante avant d'en créer une nouvelle, ou augmentez votre quota de chaînes.</>);
            setIsQuotaMessage (true);
            setShowMessageWindow (true);
            return;
           }
         }
       }
      catch (error)
       {
        console.error('Erreur lors du contrôle du quota de chaînes :', error);
       }
      finally
       {
        setShowWaitingIcon(false);
       }
     }

    setCreatedChannelId (null);
    setCreationSeq (n => n + 1);
    setCreateNewBroadcastingChannel (true);
    setShowList (false);
  };

  // Chaîne créée et question « l'activer maintenant ? » répondue : retour à la liste. La fiche
  // reste montée derrière, le temps qu'une activation demandée aille au bout.
  const handleCreationDone = () => {
    setShowList (true);
    loadBroadcastingChannels (true);
  };

  // Annuler de la fiche de création : seulement tant que la chaîne n'est pas créée (ensuite
  // la Configuration passe en édition et Annuler y recharge le formulaire).
  const handleCancelNewBroadcastingChannel = () => {
    confirmLeaveCreation(() => {
      setCreateNewBroadcastingChannel (false);
      setCreatedChannelId (null);
      setShowList (true);
      loadBroadcastingChannels (true);
    });
  };


  const renderCategoriesSelection = (): JSX.Element => {
    const categoriesNames = ['Toutes', ...categorySet.map((c) => c.name)];
    return (
      <Select choices  = {categoriesNames}
              value    = {selectedCategory}
              onChange = {handleCategoryChange}
              width    = "100%"
              height   = "28px" />
    );
  };

  const handleMessageWindowClose = () => {

    setShowMessageWindow (false)
  }

  const renderList = () : JSX.Element => (
    <>
      <div className={classNames(styles.page_header)}>
        <div className={classNames(styles.page_header_text)}>
          <h1 className={classNames(styles.page_title)}>Chaînes</h1>
          <p className={classNames(styles.page_subtitle)}>Gérez, organisez et diffusez vos chaînes.</p>
        </div>
        <div className={classNames(styles.mediaCreationButton)}>
          <button className={classNames(styles.btn, styles.btnPrimary)}
                  onClick={handleNewBroadcastingChannelCreationClick}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Créer une chaîne
          </button>
        </div>
      </div>

      <div className={classNames(styles.upper_bar)}>

        <div className={classNames(styles.upper_bar_left)}>
          {renderCategoriesSelection()}
        </div>

        <div className={classNames(styles.upper_bar_center)}>
          <Field type        = "text"
                 placeholder = "Rechercher..."
                 value       = {filter}
                 onChange    = {handleFilterChange} />
        </div>

      </div>

      <div className={classNames(styles.list_container)}>

        <div className={classNames(styles.list)}>
          <ColumnTitle titles = {[ 'Logo'  , 'Nom'  , 'Actions' , 'Mode'  , 'Actif' , 'Payant' , 'Public' ]}
                       widths = {[ '150px' , 'auto' , '150px'   , '150px' , '110px' , '110px'  , '110px'  ]} />

          {
           visibleBroadcastingChannelSet.map ( (broadcastingChannel, index) => (
                                                            <BroadcastingChannelRow key                 = {broadcastingChannel.id}
                                                                                    broadcastingChannel = {broadcastingChannel}
                                                                                    index               = {index}
                                                                                    onEdit              = {handleEditionClick}
                                                                                    onDelete            = {handleDeleteClick}
                                                                                    productArray        = {productArray} />
                                          )
                        )
          }
        </div>

      </div>

    </>
  );

  const hasOpenSheet = createNewBroadcastingChannel || editBroadcastingChannelId !== null;

  return (
    <div className={classNames(styles.root)}>
      {showWaitingIcon && <AnimatedWaitingIcon2 />}
      {showMessageWindow &&
              <Window message                 = {message}
                      messageType             = "information"
                      onClickOnValidateButton  = {handleMessageWindowClose}
                      validButtonText          = {isQuotaMessage ? 'Fermer' : undefined}
                      extraButtonText          = {isQuotaMessage ? BUY_CHANNEL_LABEL : undefined}
                      onClickOnExtraButton     = {isQuotaMessage ? () => openBuyChannel(setPurchaseNotice) : undefined}
                      width                    = {isQuotaMessage ? 'min(560px, 92vw)' : undefined}   />

             }

      {purchaseNotice !== '' &&
              <Window message                 = {<>{purchaseNotice}</>}
                      messageType             = "information"
                      onClickOnValidateButton = {() => setPurchaseNotice('')}
                      validButtonText         = "Ok"
                      zIndex                  = {700}   />
             }

      {leaveCreationAction &&
              <Window message                 = {<>Quitter la création de la chaîne ? Vous n'avez pas cliqué sur « Créer la chaîne » : la chaîne ne sera pas créée et les informations saisies seront perdues.</>}
                      messageType             = "question"
                      onClickOnValidateButton = {() => { const proceed = leaveCreationAction; setLeaveCreationAction(null); proceed(); }}
                      onClickOnCancelButton   = {() => setLeaveCreationAction(null)}
                      validButtonText         = "Oui"
                      cancelButtonText        = "Non"   />
             }

      {showDeleteConfirmation &&
              <Window message                 = {<>{`Êtes-vous sûr de vouloir supprimer la chaîne ${broadcastingChannelToDelete?.name} ? Cette opération est irréversible.`}</>}
                      messageType             = "question"
                      onClickOnValidateButton = {() => handleDeleteConfirmation(true)}
                      onClickOnCancelButton   = {() => handleDeleteConfirmation(false)}
                      validButtonText         = "Oui"
                      cancelButtonText        = "Non"   />
             }

      {/* Même espacement (20 px) entre l'en-tête, la barre de filtres et le tableau que dans
          les autres listes, où ces blocs sont directement enfants du conteneur flex `.root` :
          ce div d'enrobage doit donc reproduire sa mise en page. */}
      <div style={{ display: showList && !showCategoryManager && initialLoadDone && !singleChannel ? 'flex' : 'none', flexDirection: 'column', gap: '20px' }}>
        {renderList()}
      </div>

      {showCategoryManager && (
        <BroadcastingChannelCategoryManager nested
                                            onBackToChannels={() => setShowCategoryManager(false)} />
      )}

      {hasOpenSheet && (
        <div key={createNewBroadcastingChannel ? `new-${creationSeq}` : `channel-${editBroadcastingChannelId}`}
             style={{ display: showList ? 'none' : undefined }}>
          {
           createNewBroadcastingChannel
             ? <BroadcastingChannelSheet onCancel={handleCancelNewBroadcastingChannel}
                                         onCreated={setCreatedChannelId}
                                         onCreationDone={handleCreationDone} />
             : <BroadcastingChannelSheet broadcastingChannelToEditId={editBroadcastingChannelId as number}
                                         initialTabIndex={editInitialTabIndex}
                                         onCreateChannel={singleChannel ? handleNewBroadcastingChannelCreationClick : undefined} />
          }
        </div>
      )}
    </div>
  );
});

BroadcastingChannelList.displayName = 'BroadcastingChannelList';

export default BroadcastingChannelList;
